import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isFrontendFeatureEnabled } from "../platform/featureFlags";

function authHeaders() {
  const token = sessionStorage.getItem("syrakis_token");
  return { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function logout(navigate) {
  sessionStorage.removeItem("syrakis_token");
  document.cookie = "syrakis_token=; path=/; max-age=0; SameSite=Lax";
  navigate("/login", { replace: true });
}

function isActive(locationPath, path) {
  if (!path) return false;
  return locationPath === path || (path !== "/" && locationPath.startsWith(`${path}/`));
}

function MenuLink({ item, location, onNavigate }) {
  const active = isActive(location.pathname, item.path);
  const content = <><Icon icon={item.icon || "solar:menu-dots-linear"} className="menu-icon" /><span>{item.label}</span></>;
  if (/^https?:\/\//i.test(item.path || "")) {
    return <li><a href={item.path} className={active ? "active-page" : ""} target="_blank" rel="noreferrer" onClick={onNavigate}>{content}</a></li>;
  }
  return <li>
    <Link to={item.path || "/dashboard"} className={active ? "active-page" : ""} onClick={onNavigate} aria-current={active ? "page" : undefined}>{content}</Link>
  </li>;
}

function MenuGroup({ group, location, onNavigate }) {
  const active = group.items?.some((item) => isActive(location.pathname, item.path));
  return (
    <li className={`dropdown open ${active ? "dropdown-open" : ""}`}>
      <span className="sidebar-group-heading d-flex align-items-center gap-2 px-3 py-2 text-secondary-light small">
        <Icon icon={group.icon || "solar:list-linear"} className="sidebar-group-icon" />
        <span className="sidebar-group-label">{group.label}</span>
      </span>
      <ul className="sidebar-submenu list-unstyled">
        {(group.items || []).map((item) => <MenuLink key={item.key || item.id} item={item} location={location} onNavigate={onNavigate} />)}
      </ul>
    </li>
  );
}

function MenuSection({ section, location, onNavigate }) {
  const isPrincipal = section.key === "principal";
  return (
    <React.Fragment key={section.key}>
      {!isPrincipal && <li className="sidebar-menu-group-title" title={section.label}>
        <Icon icon={section.icon || "solar:widget-linear"} className="sidebar-section-icon" />
        <span className="sidebar-section-label">{section.label}</span>
      </li>}
      {isPrincipal
        ? (section.groups || []).flatMap((group) => group.items || []).map((item) => <MenuLink key={item.key || item.id} item={item} location={location} onNavigate={onNavigate} />)
        : (section.groups || []).map((group) => <MenuGroup key={group.key} group={group} location={location} onNavigate={onNavigate} />)}
    </React.Fragment>
  );
}

function FavoriteSection({ favorites, location, onNavigate }) {
  return (
    <>
      <li>
        <Link to="/menu-core/favoritos" className={isActive(location.pathname, "/menu-core/favoritos") ? "active-page" : ""} onClick={onNavigate}>
          <Icon icon="solar:star-linear" className="menu-icon" />
          <span>Favoritos</span>
        </Link>
      </li>
    </>
  );
}

function fallbackPageLabel(pathname) {
  const segment = pathname.split("/").filter(Boolean).pop() || "dashboard";
  return segment.replace(/[-_]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function CoreShellLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [menuVersion, setMenuVersion] = useState(0);

  useEffect(() => {
    let mounted = true;
    fetch("/api/menu", { credentials: "same-origin", headers: authHeaders() })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (!mounted || !data) return;
        setMenuData(data);
        if (data.preferences?.sidebar_collapsed !== undefined) setCollapsed(Boolean(data.preferences.sidebar_collapsed));
      })
      .catch(() => { if (mounted) setMenuData({ menu: { sections: [] }, favorites: [], preferences: {} }); });
    return () => { mounted = false; };
  }, [location.pathname, menuVersion]);

  async function addFavorite(item) {
    if (!item?.path) return;
    try {
      const response = await fetch("/api/menu/favorites", {
        method: "POST",
        credentials: "same-origin",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ label: item.label, url: item.path, route_key: item.route_key, icon: item.icon }),
      });
      if (!response.ok) throw new Error("No se pudo guardar el favorito");
      toast.success(`“${item.label}” se agregó a favoritos.`);
      setMenuVersion((value) => value + 1);
    } catch (_error) {
      toast.error("No se pudo agregar esta página a favoritos.");
    }
  }

  async function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      await fetch("/api/menu/preferences", {
        method: "PUT",
        credentials: "same-origin",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ preferences: { ...(menuData?.preferences || {}), sidebar_collapsed: next } }),
      });
    } catch (_error) {
      // La preferencia visual no debe bloquear la navegación.
    }
  }

  async function resetOnboarding() {
    if (resetting) return;
    setResetting(true);
    try {
      const response = await fetch("/api/onboarding/reset", { method: "POST", credentials: "same-origin", headers: authHeaders() });
      if (!response.ok) throw new Error("No se pudo reiniciar onboarding");
      logout(navigate);
    } catch (error) {
      console.error("[CoreShellLayout]", error);
      setResetting(false);
      window.alert("No fue posible reiniciar el onboarding.");
    }
  }

  const sections = menuData?.menu?.sections || [];
  const visibleSections = sections.map((section) => ({
    ...section,
    groups: (section.groups || []).map((group) => ({
      ...group,
      items: (group.items || [])
        .filter((item) => !item.frontend_feature || isFrontendFeatureEnabled(item.frontend_feature))
        .map((item) => item.key === "menu.admin" ? { ...item, label: "Menú plataforma" } : item),
    })).filter((group) => group.items.length),
  })).filter((section) => section.groups.length);
  const favorites = menuData?.favorites || [];
  const dashboardSections = visibleSections.filter((section) => section.key === "principal" || section.groups.some((group) => group.items.some((item) => item.route_key === "core.dashboard")));
  const nonDashboardSections = visibleSections.filter((section) => !dashboardSections.includes(section));
  const currentPage = visibleSections
    .flatMap((section) => section.groups || [])
    .flatMap((group) => group.items || [])
    .filter((item) => item.path && isActive(location.pathname, item.path))
    .sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0))[0]
    || { key: `current:${location.pathname}`, label: fallbackPageLabel(location.pathname), path: location.pathname, route_key: null };
  const currentPageIsFavorite = Boolean(favorites.some((group) => (group.items || []).some((item) => (
    currentPage.route_key ? item.route_key === currentPage.route_key : item.path === currentPage.path
  ))));

  return (
    <div className="dashboard-main-page">
      <aside className={`sidebar ${collapsed ? "active" : ""} ${mobileOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-logo">
          <Link to="/dashboard" className="text-decoration-none d-flex align-items-center gap-2">
            <Icon icon="solar:layers-minimalistic-linear" className="sidebar-brand-icon text-primary-600 text-2xl" aria-hidden="true" />
            <span className="sidebar-brand-label fw-semibold text-primary-light">Sinapso Core</span>
          </Link>
        </div>
        <button type="button" className="sidebar-close-btn bg-transparent" onClick={() => setMobileOpen(false)} aria-label="Cerrar menú"><Icon icon="iconamoon:close-light" /></button>
        <div className="sidebar-menu-area">
          <ul className="sidebar-menu list-unstyled mb-0">
            {!menuData && <li className="px-3 py-3 text-secondary-light">Cargando menú...</li>}
            {dashboardSections.map((section) => <MenuSection key={section.key} section={section} location={location} onNavigate={() => setMobileOpen(false)} />)}
            <FavoriteSection favorites={favorites} location={location} onNavigate={() => setMobileOpen(false)} />
            {nonDashboardSections.map((section) => <MenuSection key={section.key} section={section} location={location} onNavigate={() => setMobileOpen(false)} />)}
          </ul>
        </div>
      </aside>

      <div className={`dashboard-main ${collapsed ? "active" : ""}`}>
        <header className="navbar-header d-flex align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <button type="button" className={`sidebar-toggle border-0 bg-transparent p-0 ${collapsed ? "active" : ""}`} onClick={toggleCollapsed} aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}><Icon icon="solar:hamburger-menu-linear" /></button>
            <button type="button" className="sidebar-mobile-toggle border-0 bg-transparent p-0" onClick={() => setMobileOpen(true)} aria-label="Abrir menú"><Icon icon="solar:hamburger-menu-linear" /></button>
            <div className="d-flex align-items-center gap-2">
              <span className="fw-semibold text-primary-light">{currentPage?.label || "Core Platform"}</span>
              {currentPage?.path && <button
                type="button"
                className={`page-favorite-button ${currentPageIsFavorite ? "is-favorite" : ""}`}
                aria-label={currentPageIsFavorite ? "Página ya agregada a favoritos" : `Agregar ${currentPage.label} a favoritos`}
                data-tooltip={currentPageIsFavorite ? "Esta página ya está en favoritos" : "Agregar esta página a favoritos"}
                disabled={currentPageIsFavorite}
                onClick={() => addFavorite(currentPage)}
              >
                <Icon icon={currentPageIsFavorite ? "solar:star-bold" : "solar:star-linear"} aria-hidden="true" />
              </button>}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <label className="d-flex align-items-center gap-2 text-sm text-secondary-light mb-0" title="Herramienta temporal de desarrollo">
              <input type="checkbox" checked={false} onChange={resetOnboarding} disabled={resetting} />
              <span>{resetting ? "Reiniciando..." : "Reiniciar onboarding"}</span>
            </label>
            <button type="button" className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2" onClick={() => logout(navigate)}><Icon icon="solar:logout-2-linear" /><span className="d-none d-sm-inline">Cerrar sesión</span></button>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}
